import { useQuery } from '@tanstack/react-query';
import type { PortalClientStatus } from '@wally-reseller/shared';
import { Button } from '@wally-reseller/ui/button';
import { UserPlus } from 'lucide-react';
import { Link } from 'react-router';
import { api } from '@/lib/api';
import { useFormatMoney, useFormatMonth, useT } from '@/lib/i18n';
import { useMe } from './Layout';
import { StatusBadge } from './StatusBadge';

/** In pipeline order. */
const ORDER: PortalClientStatus[] = [
  'submitted',
  'evidence_requested',
  'in_progress',
  'demo_planned',
  'demo_done',
  'customer',
  'lost',
  'refused',
];

export function DashboardPage() {
  const t = useT();
  const me = useMe().data;
  const money = useFormatMoney();
  const counts = useQuery({ queryKey: ['registration-counts'], queryFn: api.registrationCounts });
  const commission = useQuery({ queryKey: ['commission-summary'], queryFn: api.commissionSummary });
  const statements = useQuery({ queryKey: ['statements'], queryFn: api.statements });
  const formatMonth = useFormatMonth();
  const last = statements.data?.[0];
  if (!me) return null;
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold">
          {t('portal.dashboard.welcome', { name: me.user.name.split(' ')[0] ?? me.user.name })}
        </h1>
        <p className="text-sm text-muted-foreground">{t('portal.dashboard.intro')}</p>
      </div>
      {commission.data && (
        <div className="space-y-2">
          <div className="text-sm font-medium">{t('portal.dashboard.commission')}</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(
              [
                ['thisMonth', commission.data.thisMonth],
                ['lastMonth', commission.data.lastMonth],
                ['thisYear', commission.data.thisYear],
                ['toDate', commission.data.toDate],
              ] as const
            ).map(([key, cents]) => (
              <div key={key} className="rounded-lg border border-border bg-background p-3">
                <div className="text-lg font-semibold tabular-nums">{money(cents)}</div>
                <div className="text-xs text-muted-foreground">
                  {t(`portal.dashboard.commission.${key}`)}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{t('portal.dashboard.commission.note')}</p>
          {last && (
            <Link to={`/decomptes/${last.id}`} className="block text-sm hover:underline">
              {t('portal.dashboard.lastStatement', {
                month: formatMonth(last.month),
                amount: money(last.totals.commission),
                status: t(`portal.statement.status.${last.status}`),
              })}
            </Link>
          )}
        </div>
      )}
      {counts.data && (
        <div className="space-y-3">
          <div className="text-sm font-medium">
            {t('portal.dashboard.total', { count: String(counts.data.total) })}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {ORDER.filter((s) => counts.data.byStatus[s]).map((s) => (
              <Link
                key={s}
                to="/leads"
                className="rounded-lg border border-border bg-background p-3 hover:bg-muted"
              >
                <div className="text-2xl font-semibold tabular-nums">{counts.data.byStatus[s]}</div>
                <StatusBadge status={s} />
              </Link>
            ))}
          </div>
        </div>
      )}
      <Button asChild>
        <Link to="/inscrire">
          <UserPlus />
          {t('portal.dashboard.register')}
        </Link>
      </Button>
    </div>
  );
}
